/**
 * Power formatting utilities (rate of energy transfer)
 */

import { isNil } from "./number";
import { FormatRegistry } from "./registry";
import type { FieldValue, FormattedValue, PowerFormat } from "./types";

/**
 * Format as Watts
 */
export const toWatts = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  return decimals === 0 ? `${Math.round(value)} W` : `${value.toFixed(decimals)} W`;
};

/**
 * Format as kilowatts
 */
export const toKilowatts = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1000).toFixed(decimals)} kW`;
};

/**
 * Format as megawatts
 */
export const toMegawatts = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1_000_000).toFixed(decimals)} MW`;
};

/**
 * Format as gigawatts
 */
export const toGigawatts = (value: number | null | undefined, decimals: number = 2): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1_000_000_000).toFixed(decimals)} GW`;
};

/**
 * Auto-scale power to the most appropriate unit
 */
export const autoScalePower = (value: number | null | undefined, decimals?: number): string | null => {
  if (isNil(value)) return null;

  const absValue = Math.abs(value);

  if (absValue >= 1_000_000_000) {
    return toGigawatts(value, decimals ?? 2);
  } else if (absValue >= 1_000_000) {
    return toMegawatts(value, decimals ?? 1);
  } else if (absValue >= 1000) {
    return toKilowatts(value, decimals ?? 1);
  } else {
    return toWatts(value, decimals ?? 0);
  }
};

/**
 * Format power according to PowerFormat specification
 */
export const formatPower = (value: FieldValue, format: PowerFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  const decimals = format.decimals;

  // If a specific unit is provided, use it
  if (format.unit) {
    switch (format.unit) {
      case "GW":
        return toGigawatts(numValue, decimals ?? 2);
      case "MW":
        return toMegawatts(numValue, decimals ?? 1);
      case "kW":
        return toKilowatts(numValue, decimals ?? 1);
      case "W":
        return toWatts(numValue, decimals ?? 0);
    }
  }

  // Default to auto-scaling unless explicitly disabled
  if (format.autoScale !== false) {
    return autoScalePower(numValue, decimals);
  }

  // If auto-scale is explicitly false and no unit specified, use base watts
  return toWatts(numValue, decimals ?? 0);
};

// Register the formatter
// biome-ignore lint/suspicious/noExplicitAny: Required for FormatRegistry type compatibility
FormatRegistry.register("power", formatPower as any);

// Convenience exports with shorter names
export const toW = toWatts;
export const toKW = toKilowatts;
export const toMW = toMegawatts;
export const toGW = toGigawatts;
